import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Image as ImageIcon, Upload, Plus, X, RefreshCw, Edit2, Sparkles, IndianRupee, Shield, ImagePlus, Edit, Youtube, AlertCircle, Tag, Loader, ArrowRight, Star, Clock, MessageCircle, CheckCircle2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { analyzeImage } from '../lib/openai';
import type { CollectibleType } from './CollectiblesList';
import { generateSlug } from '../lib/supabase';
import ImageEditor from './ImageEditor';
import CameraCapture from './CameraCapture';

interface BulkItem {
  id: string;
  images: File[];
  type: CollectibleType;
  name: string;
  description: string;
  price: string;
  youtube_url?: string;
  status: 'pending' | 'processing' | 'success' | 'error';
  error?: string;
  category_id: string;
}

interface Category {
  id: string;
  name: string;
}

function BulkUpload() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<BulkItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [showCamera, setShowCamera] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .order('name');

        if (error) throw error;
        setCategories(data || []);
        
        // Find and set Mixed Collectibles as default
        const mixedCategory = data?.find(cat => cat.name === 'Mixed Collectibles');
        if (mixedCategory) {
          setItems(prev => prev.map(item => ({
            ...item,
            category_id: item.category_id || mixedCategory.id
          })));
        }
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories');
      }
    };

    fetchCategories();
  }, []);

  const addNewItem = () => {
    if (items.length >= 10) {
      alert('Maximum 10 items allowed for bulk upload');
      return;
    }

    // Find default category (Mixed Collectibles)
    const defaultCategory = categories.find(cat => cat.name === 'Mixed Collectibles');

    setItems(prev => [...prev, {
      id: `item_${prev.length + 1}`,
      images: [],
      type: 'coin',
      name: '',
      description: '',
      price: '',
      youtube_url: '',
      status: 'pending',
      category_id: defaultCategory?.id || ''
    }]);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>, useCamera: boolean = false) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Check if there are any items
    if (items.length === 0) {
      setError('Please add an item before uploading images');
      if (useCamera) {
        if (cameraInputRef.current) cameraInputRef.current.value = '';
      } else {
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
      return;
    }

    // Ensure we have a valid current item
    if (!items[currentItemIndex]) {
      setError('Please select an item before uploading images');
      if (useCamera) {
        if (cameraInputRef.current) cameraInputRef.current.value = '';
      } else {
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
      return;
    }

    const remainingSlots = 6 - items[currentItemIndex].images.length;
    const filesToProcess = files.slice(0, remainingSlots);

    setItems(prev => {
      const updatedItems = [...prev];
      updatedItems[currentItemIndex] = {
        ...updatedItems[currentItemIndex],
        images: [...updatedItems[currentItemIndex].images, ...filesToProcess]
      };
      return updatedItems;
    });

    if (useCamera) {
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    } else {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }

    // If this is the first image, try to analyze it
    if (items[currentItemIndex].images.length === 0 && filesToProcess.length > 0) {
      try {
        const imageBase64 = await new Promise<string>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(filesToProcess[0]);
        });

        const analysis = await analyzeImage(imageBase64);
        
        setItems(prev => {
          const updated = [...prev];
          updated[currentItemIndex] = {
            ...updated[currentItemIndex],
            type: analysis.type || updated[currentItemIndex].type,
            name: analysis.name || '',
            description: analysis.description || '',
            price: analysis.suggestedPrice?.toString() || ''
          };
          return updated;
        });
      } catch (err) {
        console.error('Error analyzing image:', err);
      }
    }
  };

  const removeItem = (index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
    if (currentItemIndex >= index) {
      setCurrentItemIndex(Math.max(0, currentItemIndex - 1));
    }
  };

  const removeImage = (itemIndex: number, imageIndex: number) => {
    setItems(prev => {
      const updatedItems = [...prev];
      updatedItems[itemIndex] = {
        ...updatedItems[itemIndex],
        images: updatedItems[itemIndex].images.filter((_, i) => i !== imageIndex)
      };
      return updatedItems;
    });
  };

  const updateItem = (index: number, updates: Partial<BulkItem>) => {
    setItems(prev => prev.map((item, i) => 
      i === index ? { ...item, ...updates } : item
    ));
  };

  const handlePriceChange = (index: number, value: string) => {
    // Only allow positive numbers
    const numericValue = value.replace(/[^0-9]/g, '');
    updateItem(index, { price: numericValue });
  };

  const handleImageEdit = (editedImage: string) => {
    if (selectedImageIndex === null) return;
    
    const byteString = atob(editedImage.split(',')[1]);
    const mimeString = editedImage.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const file = new File([ab], `edited-image-${Date.now()}.jpg`, { type: mimeString });
    
    setItems(prev => {
      const updatedItems = [...prev];
      const updatedImages = [...updatedItems[currentItemIndex].images];
      updatedImages[selectedImageIndex] = file;
      updatedItems[currentItemIndex] = {
        ...updatedItems[currentItemIndex],
        images: updatedImages
      };
      return updatedItems;
    });
    
    setShowImageEditor(false);
    setSelectedImageIndex(null);
  };

  const handleCameraCapture = (imageData: string) => {
    // Check if there are any items
    if (items.length === 0) {
      setError('Please add an item before capturing an image');
      return;
    }

    // Ensure we have a valid current item
    if (!items[currentItemIndex]) {
      setError('Please select an item before capturing an image');
      return;
    }

    const byteString = atob(imageData.split(',')[1]);
    const mimeString = imageData.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const file = new File([ab], `camera-image-${Date.now()}.jpg`, { type: mimeString });
    
    setItems(prev => {
      const updatedItems = [...prev];
      updatedItems[currentItemIndex] = {
        ...updatedItems[currentItemIndex],
        images: [...updatedItems[currentItemIndex].images, file]
      };
      return updatedItems;
    });
    
    setShowCamera(false);
    
    // If this is the first image, analyze it
    if (items[currentItemIndex].images.length === 0) {
      analyzeImage(imageData)
        .then(analysis => {
          setItems(prev => {
            const updated = [...prev];
            updated[currentItemIndex] = {
              ...updated[currentItemIndex],
              type: analysis.type || updated[currentItemIndex].type,
              name: analysis.name || '',
              description: analysis.description || '',
              price: analysis.suggestedPrice?.toString() || ''
            };
            return updated;
          });
        })
        .catch(console.error);
    }
  };

  const retryAnalysis = async () => {
    if (items[currentItemIndex].images.length === 0) {
      setError('Please add at least one image to analyze');
      return;
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      const imageUrl = URL.createObjectURL(items[currentItemIndex].images[0]);
      const analysis = await analyzeImage(imageUrl);

      setItems(prev => {
        const updated = [...prev];
        updated[currentItemIndex] = {
          ...updated[currentItemIndex],
          type: analysis.type || updated[currentItemIndex].type,
          name: analysis.name || '',
          description: analysis.description || '',
          price: analysis.suggestedPrice?.toString() || ''
        };
        return updated;
      });
    } catch (err) {
      console.error('Error analyzing image:', err);
      setError('Failed to analyze image. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSubmit = async () => {
    if (items.length === 0) {
      setError('Please add at least one item');
      return;
    }

    const emptyItems = items.filter(item => item.images.length === 0);
    if (emptyItems.length > 0) {
      setError('All items must have at least one image');
      return;
    }

    setIsSubmitting(true);
    setError(null);
    let successCount = 0;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      try {
        updateItem(i, { status: 'processing' });

        const reader = new FileReader();
        const imageBase64Promise = new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
        });
        reader.readAsDataURL(item.images[0]);
        
        const imageBase64 = await imageBase64Promise;
        const analysis = await analyzeImage(imageBase64);

        // Use provided price if available, otherwise use AI-suggested price
        const price = item.price 
          ? parseInt(item.price, 10)
          : (analysis.suggestedPrice || 0);

        const { data: collectible, error: collectibleError } = await supabase
          .from('collectibles')
          .insert({
            type: analysis.type || item.type,
            name: item.name || analysis.name,
            description: item.description || analysis.description,
            price,
            youtube_url: item.youtube_url || null,
            category_id: item.category_id || null,
            slug: generateSlug(item.name || analysis.name || 'collectible')
          })
          .select()
          .single();

        if (collectibleError || !collectible) throw collectibleError;

        const imagePromises = item.images.map(async (file, index) => {
          const fileName = `${collectible.id}/${Date.now()}-${index}`;
          const { error: uploadError } = await supabase.storage
            .from('collectible-images')
            .upload(fileName, file);

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
            .from('collectible-images')
            .getPublicUrl(fileName);

          return supabase
            .from('collectible_images')
            .insert({
              collectible_id: collectible.id,
              image_url: publicUrl,
              order: index
            });
        });

        await Promise.all(imagePromises);
        updateItem(i, { status: 'success' });
        successCount++;
      } catch (err) {
        console.error('Error processing item:', err);
        updateItem(i, { 
          status: 'error',
          error: err instanceof Error ? err.message : 'Failed to process item'
        });
      }
    }

    setIsSubmitting(false);
    
    // If at least one item was uploaded successfully, mark as success
    if (successCount > 0) {
      setUploadSuccess(true);
      // Navigate to featured collections after a short delay
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 1500);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-gradient-to-b from-gray-50 to-white rounded-2xl shadow-xl p-8 border border-gray-100">
        <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-100">
          <div className="relative">
            <div className="h-12 w-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-100">
              <Upload className="h-6 w-6 text-white" />
            </div>
            <div className="absolute -top-1 -right-1 h-3 w-3 bg-primary-400 rounded-full border-2 border-white animate-pulse" />
          </div>
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">
              Bulk Upload
            </h1>
            <p className="text-gray-500">Upload up to 10 items at once</p>
          </div>
        </div>

        {uploadSuccess && (
          <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg text-primary-600 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5" />
            Upload successful! Redirecting to featured collections...
          </div>
        )}

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            <p>{error}</p>
          </div>
        )}

        <div className="space-y-4 mb-6">
          {/* Image Upload Options */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <button
              onClick={() => cameraInputRef.current?.click()}
              disabled={isSubmitting}
              className="flex flex-col items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Camera className="h-6 w-6 text-gray-400" />
              <span className="text-sm text-gray-600">Use Camera</span>
            </button>
            
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isSubmitting}
              className="flex flex-col items-center justify-center gap-2 p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ImageIcon className="h-6 w-6 text-gray-400" />
              <span className="text-sm text-gray-600">Upload Image</span>
            </button>
          </div>

          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            multiple
            onChange={(e) => handleFileSelect(e, false)}
          />
          
          <input
            type="file"
            ref={cameraInputRef}
            className="hidden"
            accept="image/*"
            capture="environment"
            onChange={(e) => handleFileSelect(e, true)}
          />

          {items.map((item, index) => (
            <div 
              key={item.id}
              className={`border rounded-xl p-6 relative transition-all ${
                index === currentItemIndex
                  ? 'border-primary-500 bg-gradient-to-br from-primary-50/50 to-primary-50/50 shadow-md'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  {item.status === 'pending' && (
                    <div className="h-6 w-6 text-gray-400">•</div>
                  )}
                  {item.status === 'processing' && (
                    <Loader className="h-6 w-6 text-primary-600 animate-spin" />
                  )}
                  {item.status === 'success' && (
                    <CheckCircle2 className="h-6 w-6 text-primary-500" />
                  )}
                  {item.status === 'error' && (
                    <AlertCircle className="h-6 w-6 text-red-500" />
                  )}
                </div>
                
                <div className="flex-grow">
                  <div className="flex items-center gap-2 mb-4">
                    <h3 className="font-medium text-gray-900">
                      Item {index + 1}
                    </h3>
                    <button
                      onClick={() => setCurrentItemIndex(index)}
                      className={`text-sm px-3 py-1.5 rounded-full transition-all ${
                        index === currentItemIndex
                          ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-sm'
                          : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                      }`}
                    >
                      {index === currentItemIndex ? 'Selected' : 'Select'}
                    </button>
                  </div>

                  {index === currentItemIndex && (
                    <>
                      <div className="mb-4">
                        {/* Move images section to the top */}
                        <div className="flex flex-wrap gap-2 mb-4">
                          {item.images.map((file, imageIndex) => (
                            <div
                              key={imageIndex}
                              className="relative w-20 h-20 bg-gray-100 rounded-lg ring-1 ring-gray-200 group"
                            >
                              <img
                                src={URL.createObjectURL(file)}
                                alt={`Preview ${imageIndex + 1}`}
                                className="w-full h-full object-cover rounded-lg"
                              />
                              {!isSubmitting && (
                                <button
                                  onClick={() => removeImage(index, imageIndex)}
                                  className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg"
                                >
                                  <X className="h-4 w-4 text-white" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Category
                            </label>
                            <select
                              value={item.category_id}
                              onChange={(e) => updateItem(index, { category_id: e.target.value })}
                              className="w-full rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                              required
                            >
                              <option value="">Select a category</option>
                              {categories.map(category => (
                                <option key={category.id} value={category.id}>
                                  {category.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Type
                            </label>
                            <select
                              value={item.type}
                              onChange={(e) => updateItem(index, { type: e.target.value as CollectibleType })}
                              className="w-full rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                            >
                              <option value="coin">Coin</option>
                              <option value="note">Note</option>
                            </select>
                          </div>
                        </div>

                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-1">
                            <label className="block text-sm font-medium text-gray-700">
                              Name
                            </label>
                            {item.images.length > 0 && (
                              <button
                                type="button"
                                onClick={retryAnalysis}
                                disabled={isAnalyzing}
                                className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-primary-50 text-primary-700 rounded hover:bg-primary-100 transition-colors"
                              >
                                {isAnalyzing ? (
                                  <>
                                    <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                                    Analyzing...
                                  </>
                                ) : (
                                  <>
                                    <Sparkles className="h-3.5 w-3.5" />
                                    Rewrite with AI
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                          <input
                            type="text"
                            value={item.name}
                            onChange={(e) => updateItem(index, { name: e.target.value })}
                            placeholder="Enter name (optional)"
                            className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                          />
                        </div>

                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <textarea
                            value={item.description}
                            onChange={(e) => updateItem(index, { description: e.target.value })}
                            placeholder="Enter description (optional)"
                            rows={3}
                            className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                          />
                        </div>

                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Price (₹)
                          </label>
                          <div className="relative">
                            <input
                              type="text"
                              value={item.price}
                              onChange={(e) => handlePriceChange(index, e.target.value)}
                              placeholder="Enter price (optional)"
                              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                            />
                            <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                          </div>
                        </div>

                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            YouTube Video URL (optional)
                          </label>
                          <div className="relative">
                            <input
                              type="url"
                              value={item.youtube_url}
                              onChange={(e) => updateItem(index, { youtube_url: e.target.value })}
                              placeholder="e.g., https://youtube.com/watch?v=..."
                              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:border-primary-500 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                              pattern="^https?:\/\/(www\.)?(youtube\.com\/(watch\?v=|shorts\/)|youtu\.be\/)[a-zA-Z0-9_-]{11}$"
                            />
                            <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                          </div>
                        </div>
                      </div>

                      {item.error && (
                        <p className="text-sm text-red-600 mt-1">{item.error}</p>
                      )}
                    </>
                  )}
                </div>

                {!isSubmitting && (
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    className="absolute top-4 right-4 text-gray-400 hover:text-red-500 p-1"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {items.length < 10 && !isSubmitting && (
          <button
            onClick={addNewItem}
            className="w-full bg-gradient-to-r from-primary-50 to-primary-50 border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-primary-500 hover:from-primary-100 hover:to-primary-100 transition-all mb-8 flex items-center justify-center gap-3 group"
          >
            <Plus className="h-5 w-5 text-primary-500" />
            <span className="text-gray-600 group-hover:text-primary-600 font-medium">Add Another Item</span>
            <ArrowRight className="h-5 w-5 text-primary-500 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        )}

        <div className="flex gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex-1 px-6 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={items.length === 0 || isSubmitting}
            className="flex-1 px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors disabled:bg-primary-300 disabled:cursor-not-allowed font-medium"
          >
            {isSubmitting ? 'Processing...' : 'Upload All'}
          </button>
        </div>
      </div>

      {showImageEditor && selectedImageIndex !== null && (
        <ImageEditor
          imageUrl={URL.createObjectURL(items[currentItemIndex].images[selectedImageIndex])}
          onSave={handleImageEdit}
          onClose={() => {
            setShowImageEditor(false);
            setSelectedImageIndex(null);
          }}
        />
      )}

      {showCamera && (
        <CameraCapture
          onCapture={handleCameraCapture}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
}

export default BulkUpload;